import { Component, OnDestroy, inject } from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterOutlet,
} from '@angular/router';
import { CommonModule } from '@angular/common';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Store } from '@ngrx/store';
import {
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  Subscription,
} from 'rxjs';
import { commonFeature } from './shared/store/reducers';
import { setLoading } from './shared/store/actions';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, CommonModule, MatProgressSpinnerModule],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnDestroy {
  private readonly overlayShowDelayMs = 150;
  private readonly overlayMinVisibleMs = 250;

  private iconRegistry = inject(MatIconRegistry);
  private sanitizer = inject(DomSanitizer);
  private store = inject(Store);
  private router = inject(Router);

  title = 'chatbot-webapp';

  loading$: Observable<boolean>;

  private sub = new Subscription();

  constructor() {
    this.iconRegistry.registerFontClassAlias(
      'material-symbols',
      'material-symbols-outlined',
    );
    this.iconRegistry.addSvgIcon(
      'instagram',
      this.sanitizer.bypassSecurityTrustResourceUrl('assets/svg/instagram.svg'),
    );
    this.iconRegistry.addSvgIcon(
      'messenger',
      this.sanitizer.bypassSecurityTrustResourceUrl('assets/svg/messenger.svg'),
    );
    this.iconRegistry.addSvgIcon(
      'whatsapp',
      this.sanitizer.bypassSecurityTrustResourceUrl('assets/svg/whatsapp.svg'),
    );

    const rawLoading$ = combineLatest([
      this.store.select(commonFeature.selectHttpCount).pipe(map((x) => x > 0)),
      this.store.select(commonFeature.selectLoading),
    ]).pipe(
      map(([http, loading]) => http || loading),
      distinctUntilChanged(),
    );

    this.loading$ = new Observable<boolean>((subscriber) => {
      let currentVisible = false;
      let visibleSince = 0;
      let showTimeout: ReturnType<typeof setTimeout> | null = null;
      let hideTimeout: ReturnType<typeof setTimeout> | null = null;

      const clearTimers = () => {
        if (showTimeout) {
          clearTimeout(showTimeout);
          showTimeout = null;
        }
        if (hideTimeout) {
          clearTimeout(hideTimeout);
          hideTimeout = null;
        }
      };

      const sub = rawLoading$.subscribe((isLoading) => {
        clearTimers();

        if (isLoading) {
          if (currentVisible) {
            subscriber.next(true);
            return;
          }

          showTimeout = setTimeout(() => {
            currentVisible = true;
            visibleSince = Date.now();
            subscriber.next(true);
          }, this.overlayShowDelayMs);

          return;
        }

        if (!currentVisible) {
          subscriber.next(false);
          return;
        }

        const elapsed = Date.now() - visibleSince;
        const remainingVisibleTime = Math.max(
          this.overlayMinVisibleMs - elapsed,
          0,
        );

        hideTimeout = setTimeout(() => {
          currentVisible = false;
          subscriber.next(false);
        }, remainingVisibleTime);
      });

      return () => {
        clearTimers();
        sub.unsubscribe();
      };
    });

    this.sub = this.router.events
      .pipe(
        filter(
          (
            event,
          ): event is
            | NavigationStart
            | NavigationEnd
            | NavigationCancel
            | NavigationError =>
            event instanceof NavigationStart ||
            event instanceof NavigationEnd ||
            event instanceof NavigationCancel ||
            event instanceof NavigationError,
        ),
      )
      .subscribe((event) => {
        if (event instanceof NavigationStart) {
          this.store.dispatch(setLoading({ loading: true }));
        } else {
          this.store.dispatch(setLoading({ loading: false }));
        }
      });
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }
}
